<template>
  <div class="nav-menu-desktop flex--col-start-center" ref="menu">
    <div class="nav-menu-desktop__background"></div>
    <div
      class="nav-menu-desktop__main-menu text--clr-black"
      :class="{ 'nav-menu-desktop__main-menu--root': root }"
    >
      <template v-for="(item, index) in getRootMenuItems">
        <button
          v-if="item.__typename === 'NavEntry'"
          :key="index"
          @click="event => triggerMenuActive(item, true, 'click', event)"
          @mouseenter="() => triggerMenuActive(item, active, 'hover')"
          @mouseleave="
            () => (active ? initDeactivation() : abortMenuActivation())
          "
          class="nav-menu-desktop__main-menu-item"
          :class="{
            'nav-menu-desktop__main-menu-item--selected':
              currentEntry && currentEntry.sys.id === item.sys.id,
            'nav-menu-desktop__main-menu-item--active': active
          }"
        >
          <span
            class="text-commerce-intro text--w-regular text--uppercase"
            :style="{ color: uiColor }"
            >{{ item.title }}</span
          >
        </button>
        <LinkTag
          v-else-if="item.__typename === 'MenuItem'"
          :key="index"
          :link="item.externalLink"
          @mouseenter.native="() => (active ? initDeactivation(true) : null)"
          @click.native="() => handleItemInteraction(item, 'click')"
          class="nav-menu-desktop__main-menu-item flex--col-start-center"
        >
          <span
            class="text-commerce-intro text--w-regular text--uppercase"
            :style="{ color: uiColor }"
            >{{ item.title }}</span
          ></LinkTag
        >
      </template>
    </div>
    <div
      v-if="root"
      class="nav-menu-desktop__dropdown"
      :class="{ 'nav-menu-desktop__dropdown--active': active }"
      @mouseenter="() => (active ? resetDeactivationTimeout() : null)"
      @mouseleave="() => (active ? initDeactivation() : null)"
    >
      <div
        class="nav-menu-desktop__dropdown-frame"
        :style="{ height: `${menuItemHeight + 80}px` }"
        :class="{ 'nav-menu-desktop__dropdown-frame--active': active }"
      >
        <div
          v-for="(entry, index) in getRootMenuItems"
          class="nav-menu-desktop__dropdown-content container flex--row-center-start"
          :class="{
            'nav-menu-desktop__dropdown-content--active':
              currentEntry && currentEntry.sys.id === entry.sys.id
          }"
          :ref="`root_entry_${entry.sys.id}`"
          :key="'root_entry_' + index"
        >
          <div class="nav-menu-desktop__dropdown-section">
            <div class="text-commerce-heading-2 text--clr-black mb-20">
              {{ entry.title }}
            </div>
            <NavigatonMenuItemsList :content="entry" />
          </div>
          <div
            v-for="index in 3"
            :key="index"
            class="nav-menu-desktop__dropdown-section"
          >
            <PromoSection
              :title="entry[`promo${index}Title`]"
              :reference="entry[`promo${index}Reference`]"
              :appearance="entry[`promo${index}Appearance`] || ''"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import GTMAPI from "@/integration/GTMAPI.js"

import NavigationMenuMixin from "./NavigationMenuMixin.js"

const mouseOverDelay = 200
const mouseOutTimeout = 500

export default {
  name: "NavigationMenuDesktop",

  mixins: [NavigationMenuMixin],

  props: {
    root: Boolean,
    uiColor: String
  },

  data: () => ({
    activationTriggerTimeout: null,
    deactivationTimeout: null,
    menuItemHeight: 0
  }),

  mounted() {
    this.$nextTick(this.calcMenuItemHeights())
  },

  methods: {
    calcMenuItemHeights() {
      this.getRootMenuItems.forEach(item => {
        const el = this.$refs[`root_entry_${item.sys.id}`]?.[0]
        const height = (el && el.offsetHeight) || 0
        if (height > this.menuItemHeight) {
          this.menuItemHeight = height
        }
      })
    },

    initDeactivation(instant = false) {
      const _delay = instant ? 0 : mouseOutTimeout
      this.deactivationTimeout = setTimeout(() => {
        this.$emit("activated", false)
      }, _delay)
    },

    resetDeactivationTimeout() {
      clearTimeout(this.deactivationTimeout)
      this.deactivationTimeout = null
    },

    handleItemInteraction(/* item, interactionType */) {
      //   // GTMAPI.trackEvent("navigation.menu.item_selected", {
      //   //   item_title: item.title,
      //   //   interaction_type: interactionType
      // })
      GTMAPI.trackEvent("navigation.menu.loaded", {
        nav_entry_id: this.currentEntry.sys.id,
        nav_entry_title: this.currentEntry.title
      })
    },

    triggerMenuActive(item, instant = false, interactionType, event = {}) {
      // If there is a deactivation timer, kill it
      if (this.deactivationTimeout) {
        this.resetDeactivationTimeout()
      }

      const _delay = instant ? 0 : mouseOverDelay

      if (
        interactionType == "click" &&
        event &&
        event.pointerType == "mouse" &&
        item.externalLink
      ) {
        this.$router.push(item.externalLink)
      }

      this.calcMenuItemHeights()

      // If there is no activation trigger timeout, create one
      if (!this.activationTriggerTimeout && !instant) {
        this.activationTriggerTimeout = setTimeout(() => {
          this.currentEntry = item
          this.handleItemInteraction(item, interactionType)
          this.$emit("activated", true)
          this.activationTriggerTimeout = null
        }, _delay)
      } else {
        this.currentEntry = item
        this.handleItemInteraction(item, interactionType)
      }
    },

    abortMenuActivation() {
      clearTimeout(this.activationTriggerTimeout)
      this.activationTriggerTimeout = null
    }
  },

  watch: {
    active: {
      handler: function(newVal, oldVal) {
        if (newVal !== oldVal) {
          if (!newVal) {
            this.currentEntry = null
          }
        }
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss">
.nav-menu-desktop {
  position: relative;
  height: $menu-height-lg;
  width: 100vw;
  z-index: 1;

  &__background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    // background-color: getcolour(bleach_white);
  }

  &__main-menu {
    position: relative;
    height: 100%;
    width: 100%;
    max-width: $bleach-site-max-width;
    padding: 0;
    // background-color: lightcoral;
    pointer-events: all;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: stretch;
    z-index: 1;

    &--root {
      span {
        opacity: 0;
      }
    }
  }

  &__main-menu-item {
    position: relative;
    background-color: transparent;
    border: none;
    outline: none;
    cursor: pointer;
    padding: 4px 16px 0;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;

    &::after {
      content: "";
      position: absolute;
      width: 100%;
      height: 4px;
      bottom: 0;
      left: 0;
      background-color: getcolour(bleach_grey_40);
      opacity: 0;
      transition: opacity 0.05s linear;
    }

    &:hover {
      &::after {
        opacity: 1;
      }
    }

    &--selected {
      &::after {
        background-color: getcolour(bleach_lime);
        opacity: 1;
      }
    }

    &--active {
    }

    // &:last-child {
    //   margin-right: unset;
    // }
  }

  &__dropdown {
    z-index: 0;
    position: absolute;
    top: 100%;
    width: 100%;
    opacity: 0;
    overflow: hidden;
    transition: opacity 0s linear 0.2s;

    @include media-breakpoint-up(md) {
      height: calc(100vh - #{$nav-height-md});
    }

    @include media-breakpoint-up(lg) {
      height: calc(100vh - #{$nav-height-lg});
    }

    @include media-breakpoint-up(xl) {
      height: calc(100vh - #{$nav-height-xl});
    }

    &--active {
      opacity: 1;
      transition: opacity 0s linear;
    }
  }

  &__dropdown-frame {
    pointer-events: all;
    width: 100%;
    background-color: getcolour(bleach_offwhite);
    // padding-top: 30px;
    // padding-bottom: 30px;
    transform: translateY(-100%);
    transition: transform 0.2s cubic-bezier(0.65, 0, 0.35, 1);

    &--active {
      transform: translateY(0);
    }
  }

  &__dropdown-content {
    position: absolute;
    opacity: 0;
    top: 24px;
    left: 50%;
    transform: translateX(-50%);
    transition: opacity 0.1s linear,
      transform 0.2s cubic-bezier(0.65, 0, 0.35, 1);
    pointer-events: none;

    &--active {
      opacity: 1;
      transition: opacity 0.1s linear 0.2s;
      pointer-events: all;
    }

    // &--before {
    //   transform: translateX(-150%);
    // }

    // &--after {
    //   transform: translateX(150%);
    // }
  }

  &__dropdown-section {
    $section-count: 4;
    $section-margin: 32px;

    flex: 0 0
      calc(
        (100% / #{$section-count}) -
          (#{$section-margin} * ((#{$section-count} - 1) / #{$section-count}))
      );
    width: calc(
      (100% / #{$section-count}) -
        (#{$section-margin} * ((#{$section-count} - 1) / #{$section-count}))
    );
    margin-right: $section-margin;

    &:last-child {
      margin-right: unset;
    }
  }
}
</style>
